/**
 * Utilities for the internal-link format: [text](#<numericId>) — the target is
 * Tahiti's `pms_word.id` primary key, verified against live devdb (BUILD-368):
 * sampling 100 real words / 706 link targets, 703 were `#<id>` (only 3 were
 * bare-word legacy outliers). `slug` (a client-only `slugify(title)`) is never
 * the target — it was a wrong assumption in earlier code. External links
 * ([text](https://…)) are left untouched, for TipTap/marked to render as
 * normal anchors.
 *
 * Resolution (by numeric id) and colouring are the only concerns left here —
 * since the TipTap migration (Point 7), rendering and DOM serialization live in
 * the editor's own extensions (see editor/word-link-mark.ts and
 * lib/markdown-tiptap.ts). This module is still used by view-mode-only surfaces
 * ("Voir aussi", the sidebar/mosaic dominant-link colour) and by the quick-add
 * auto-linker.
 */
import type { NoyauCode, NoyauKey, WordSummary } from '@/schemas/word';

export const NOYAU_CODE_TO_KEY: Record<NoyauCode, NoyauKey> = {
  SGP: 'bleunuit',
  SPG: 'bleuciel',
  PSG: 'vert',
  PGS: 'jaune',
  GPS: 'rouge',
  GSP: 'rose',
};

export const NOYAU_LINK_COLOR: Record<NoyauKey, string> = {
  bleunuit: 'var(--color-pms-bleu-nuit)',
  bleuciel: 'var(--color-pms-bleu-ciel)',
  vert: 'var(--color-pms-vert)',
  jaune: 'var(--color-pms-jaune-deep)',
  rouge: 'var(--color-pms-rouge)',
  rose: 'var(--color-pms-rose)',
};

// Internal targets are `#<numericId>`; external targets carry a URL scheme
// (http:, https:, mailto:, …) and are left alone.
const EXTERNAL_TARGET_RE = /^[a-z][a-z0-9+.-]*:/i;

/** Whether a markdown link target is an external URL (has a scheme), not an internal id reference. */
export function isExternalTarget(target: string): boolean {
  return EXTERNAL_TARGET_RE.test(target);
}

// Schemes we allow to reach an `href`. Anything else (notably `javascript:` and
// `data:`) is a script-execution / phishing vector once rendered as a live
// anchor, so it is dropped. Word `content` comes from Tahiti and is user-edited,
// i.e. an untrusted stored-input boundary.
const SAFE_URL_SCHEME_RE = /^(https?|mailto):/i;

/**
 * Return `href` when its scheme is safe to render as an anchor target, else
 * null. Scheme-relative (`//host`) and unknown-scheme targets are rejected;
 * callers should render such links inert (no `href`) rather than trusting them.
 */
export function safeExternalHref(href: string): string | null {
  const trimmed = href.trim();
  if (!SAFE_URL_SCHEME_RE.test(trimmed)) return null;
  return trimmed;
}

/** Extract the numeric id from an internal link target (`#16222` → `'16222'`), or null when malformed (a legacy/hand-typed target — renders dangling, no fallback). */
export function parseWordLinkId(target: string): string | null {
  const match = /^#(\d+)$/.exec(target);
  return match?.[1] ?? null;
}

/** Resolve a link target's numeric id to its word. Null when dangling (no matching word). */
export function resolveWordLink(id: string, wordList: WordSummary[]): WordSummary | null {
  return wordList.find((w) => w.numericId === id) ?? null;
}

const INTERNAL_LINK_RE = /\[([^\]]+)\]\(([^)]+)\)/g;

/**
 * The numeric ids targeted by every internal `[text](#<id>)` link in `content`
 * (external links and malformed/legacy targets skipped), deduped, in order of
 * first appearance. Used to resolve a word's own links by exact id lookup
 * ({@link wordsApi.getByIds}) instead of relying on the default (capped,
 * alphabetical) word list.
 */
export function extractInternalLinkIds(content: string): string[] {
  const seen = new Set<string>();
  const ids: string[] = [];
  let m = INTERNAL_LINK_RE.exec(content);
  while (m !== null) {
    const target = m[2];
    const id = target && !isExternalTarget(target) ? parseWordLinkId(target) : null;
    if (id && !seen.has(id)) {
      seen.add(id);
      ids.push(id);
    }
    m = INTERNAL_LINK_RE.exec(content);
  }
  return ids;
}

/** One "Voir aussi" entry: its display label, and the numeric id it links to (null for a bare/legacy label with no link). */
export interface SeeAlsoEntry {
  label: string;
  id: string | null;
}

const SEE_ALSO_LINE_RE = /^\[([^\]]+)\]\(#(\d+)\)$/;

/** Parse the `see_also` markdown into its entries — each line is `- [label](#id)` (or a bare `- label`, tolerated but never resolved). */
export function parseSeeAlsoEntries(seeAlso: string): SeeAlsoEntry[] {
  return seeAlso
    .split('\n')
    .map((line) => line.replace(/^[-*]\s*/, '').trim())
    .filter(Boolean)
    .map((line) => {
      const match = SEE_ALSO_LINE_RE.exec(line);
      return match ? { label: match[1] ?? '', id: match[2] ?? null } : { label: line, id: null };
    });
}

/** Serialize "Voir aussi" entries back into the `see_also` markdown bullet list. */
export function serializeSeeAlsoEntries(entries: SeeAlsoEntry[]): string {
  return entries.map((e) => (e.id ? `- [${e.label}](#${e.id})` : `- ${e.label}`)).join('\n');
}

// ── Quick-add scan ────────────────────────────────────────────────────────────

export interface QuickAddMatch {
  id: string;
  wordSummary: WordSummary;
  matchText: string;
  offset: number;
  context: string;
}

/** Byte ranges already inside a markdown link `[...](...)`, so they're never double-linked. */
function findTakenRanges(content: string): [number, number][] {
  const ranges: [number, number][] = [];
  const re = /\[([^\]]+)\]\([^)]+\)/g;
  let m = re.exec(content);
  while (m !== null) {
    ranges.push([m.index, m.index + m[0].length]);
    m = re.exec(content);
  }
  return ranges;
}

/** Byte ranges inside a fenced code block — titles appearing in code are never auto-linked. */
function findCodeRanges(content: string): [number, number][] {
  const ranges: [number, number][] = [];
  const re = /```[^\n]*\n[\s\S]*?```/g;
  let m = re.exec(content);
  while (m !== null) {
    ranges.push([m.index, m.index + m[0].length]);
    m = re.exec(content);
  }
  return ranges;
}

function escapeRegex(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/** Find all occurrences of word titles in `content` that are not already linked or inside code. */
export function scanForWordLinks(content: string, wordList: WordSummary[]): QuickAddMatch[] {
  const matches: QuickAddMatch[] = [];
  const excludedRanges = [...findTakenRanges(content), ...findCodeRanges(content)];

  for (const word of wordList) {
    if (!word.title) continue;
    const titleRe = new RegExp(`\\b${escapeRegex(word.title)}\\b`, 'gi');
    let m = titleRe.exec(content);
    while (m !== null) {
      const start = m.index;
      const end = start + m[0].length;
      if (!excludedRanges.some(([s, e]) => start >= s && end <= e)) {
        const ctxStart = Math.max(0, start - 50);
        const ctxEnd = Math.min(content.length, end + 50);
        const context =
          (ctxStart > 0 ? '…' : '') +
          content.slice(ctxStart, ctxEnd) +
          (ctxEnd < content.length ? '…' : '');
        matches.push({
          id: `${start}-${word.id}`,
          wordSummary: word,
          matchText: m[0],
          offset: start,
          context,
        });
      }
      m = titleRe.exec(content);
    }
  }

  return matches.sort((a, b) => a.offset - b.offset);
}

/** Apply a subset of matches to `content` (reverse-offset order to preserve earlier positions). */
export function applyQuickAddMatches(content: string, matches: QuickAddMatch[]): string {
  const sorted = [...matches].sort((a, b) => b.offset - a.offset);
  let next = content;
  for (const match of sorted) {
    const before = next.slice(0, match.offset);
    const after = next.slice(match.offset + match.matchText.length);
    next = `${before}[${match.matchText}](#${match.wordSummary.numericId})${after}`;
  }
  return next;
}
